import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'sonner';
import { IconAlertTriangle } from '@rossum/ui/icons/tabler';
import { Box, Stack, SvgIcon, Typography } from '@rossum/ui/material';
import { useIsFeatureEnabled } from '../feature-enablement/featureEnablement';
import { AttachmentDropOverlay } from './attachments/AttachmentDropOverlay';
import { ChatWelcomeScreen } from './ChatWelcomeScreen';
import { pushChatSession } from './dataLayer';
import { FieldWelcomeScreen } from './FieldWelcomeScreen';
import { ChatHeader } from './header/ChatHeader';
import {
  chatStore,
  isStreamingStatus,
  pendingApproval,
  pendingToolQuestion,
  setMessages,
  useActiveChatIds,
  useChatClient,
  useChatClientState,
  useChatMessages,
  useChatStore,
  useUnseenChatIds,
} from './hooks/chatStore';
import { getChatMetadata } from './hooks/UseAgentChat/schemas';
import { submitFeedback } from './hooks/useChatApi';
import { useChatAttachments } from './hooks/useChatAttachments';
import {
  useCreateNewChatClient,
  useLoadChatClient,
} from './hooks/useCreateChatClient';
import { AgentQuestionForm } from './input/AgentQuestionForm';
import { ChatBottomPanel } from './input/ChatBottomPanel';
import { ToolApprovalCard } from './input/ToolApprovalCard';
import { ChatMessageList } from './messages/ChatMessageList';
import { TaskList } from './messages/TaskList';
import { ChatDrawerShell } from './shared/ChatDrawerShell';
import type { DocumentContent, ImageContent } from './types';
import {
  AgentQuestionAnswer,
  formatAgentQuestionAnswersForMessage,
  formatAskUserAnswer,
} from './utils/agentQuestions';

type ChatPanelProps = {
  open: boolean;
  onClose: () => void;
};

type AgentQuestionAnswers = Record<number, AgentQuestionAnswer>;

export const ChatPanelContent = ({ onClose }: ChatPanelProps) => {
  const intl = useIntl();
  const chatId = useChatStore(s => s.drawerChatId);

  useEffect(() => {
    pushChatSession(chatId);
  }, [chatId]);

  const client = useChatClient(chatId);
  const activeChatIds = useActiveChatIds();
  const unseenChatIds = useUnseenChatIds();
  const {
    status,
    error,
    agentQuestion,
    tasks,
    outputFiles,
    lastStreamFinished,
  } = useChatClientState(chatId);

  const isChatWriteEnabled = useIsFeatureEnabled('mr-fabry-write');
  const isMrFabry = useIsFeatureEnabled('mr-fabry-coupacabry');

  const [mcpReadWriteToggle, setMcpReadWriteToggle] = useState(false); // unchanged

  const mcpReadWrite = isChatWriteEnabled ? mcpReadWriteToggle : false;
  const setMcpReadWrite = isChatWriteEnabled
    ? setMcpReadWriteToggle
    : undefined;

  // Off, mr-fabry asks before each write — for everyone; `mr-fabry-write` only unlocks auto mode.
  // The legacy agent refuses writes instead.
  const mcpMode = mcpReadWrite ? 'read-write' : isMrFabry ? 'ask' : 'read-only';

  const {
    create: createNew,
    isLoading: isCreatingNew,
    error: createNewError,
  } = useCreateNewChatClient();

  const { load: loadChat, isLoading: isLoadingHistory } = useLoadChatClient();

  const isInitializing = isCreatingNew || isLoadingHistory;
  const initError = createNewError instanceof Error ? createNewError : null;

  const messages = useChatMessages(chatId);

  const attachments = useChatAttachments({
    disabled: isInitializing,
  });

  const isStreaming = isStreamingStatus(status);
  const fieldDraft = useChatStore(s => s.fieldDraft);

  // mr-fabry's question lives on the last message; the legacy agent's in the session. Hidden while
  // a run streams, as answering it is what starts the next one.
  const toolQuestion = isStreaming
    ? null
    : pendingToolQuestion(messages.at(-1));
  const questions =
    agentQuestion?.questions ?? (toolQuestion ? [toolQuestion.question] : null);

  const approval = pendingApproval(messages.at(-1));

  const handleApproval = useCallback(
    (approved: boolean) => {
      if (!client || !approval) return;
      client
        .respondToApproval(approval, approved, mcpMode)
        .catch(() =>
          toast.error(
            intl.formatMessage({ id: 'features.chat.toolApproval.failed' })
          )
        );
    },
    [client, approval, intl, mcpMode]
  );

  const updateMessageFeedback = useCallback(
    (messageIndex: number, feedback: boolean | undefined) =>
      setMessages(prev =>
        prev.map((message, index) =>
          index === messageIndex
            ? {
                ...message,
                metadata: {
                  ...getChatMetadata(message.metadata),
                  feedback,
                },
              }
            : message
        )
      ),
    []
  );

  const handleSend = useCallback(
    (
      content: string,
      images?: ImageContent[],
      documents?: DocumentContent[]
    ) => {
      if (client) {
        client.sendAgentMessage(content, mcpMode, { images, documents });
        return;
      }

      createNew(newClient => {
        chatStore.getState().setDrawerClient(newClient);
        newClient.sendAgentMessage(content, mcpMode, { images, documents });
      });
    },
    [client, createNew, mcpMode]
  );

  const handleStop = useCallback(() => {
    client?.stop();
  }, [client]);

  const onCreateNew = useCallback(() => {
    chatStore.getState().setDrawerClient(null);
  }, []);

  const handleFeedback = useCallback(
    async (messageIndex: number, isPositive: boolean) => {
      if (!chatId) return;

      const previousFeedback = getChatMetadata(
        chatStore.getState().sessions[chatId]?.messages[messageIndex]?.metadata
      ).feedback;

      updateMessageFeedback(messageIndex, isPositive);

      try {
        await submitFeedback(
          chatId,
          messageIndex,
          isPositive,
          window.location.href
        );
      } catch {
        updateMessageFeedback(messageIndex, previousFeedback);
        toast.error(
          intl.formatMessage({ id: 'features.chat.feedback.failed' })
        );
      }
    },
    [chatId, updateMessageFeedback, intl]
  );

  const handleSelectChat = useCallback(
    (selectedChatId: string) => {
      loadChat(selectedChatId, newClient => {
        chatStore.getState().setDrawerClient(newClient);
      });
    },
    [loadChat]
  );

  const handleAgentQuestionSubmit = useCallback(
    (answers: AgentQuestionAnswers) => {
      if (agentQuestion) {
        handleSend(
          formatAgentQuestionAnswersForMessage(agentQuestion.questions, answers)
        );
        return;
      }
      if (client && toolQuestion) {
        client.answerToolQuestion(
          toolQuestion.toolCallId,
          formatAskUserAnswer(toolQuestion.question, answers[0]),
          mcpMode
        );
      }
    },
    [agentQuestion, client, handleSend, mcpMode, toolQuestion]
  );

  const canChangeMcpMode = !isStreaming;

  return (
    <Stack
      sx={{ height: '100%', position: 'relative' }}
      {...attachments.dragHandlers}
    >
      {attachments.isDragging && attachments.canAddMore && (
        <AttachmentDropOverlay />
      )}
      <ChatHeader
        mcpReadWrite={mcpReadWrite}
        onMcpModeChange={setMcpReadWrite}
        canChangeMcpMode={canChangeMcpMode}
        onCreateNew={onCreateNew}
        onClose={onClose}
        onSelectChat={handleSelectChat}
        currentChatId={chatId}
        activeChatIds={activeChatIds}
        unseenChatIds={unseenChatIds}
        outputFiles={outputFiles}
      />

      {mcpReadWrite && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 2,
            py: 0.75,
            bgcolor: theme =>
              theme.palette.mode === 'dark'
                ? 'rgba(237, 108, 2, 0.15)'
                : 'rgba(255, 152, 0, 0.12)',
            color: 'warning.dark',
          }}
        >
          <SvgIcon fontSize="small">
            <IconAlertTriangle />
          </SvgIcon>
          <Typography variant="caption" fontWeight={500}>
            {intl.formatMessage({
              id: isMrFabry
                ? 'features.chat.header.autoMode.warning'
                : 'features.chat.header.writeMode.warning',
            })}
          </Typography>
        </Stack>
      )}

      {messages.length === 0 && !isStreaming ? (
        // The drawer opened on a field has its own empty state: the general tips describe a
        // conversation the user did not come here for.
        fieldDraft ? (
          <FieldWelcomeScreen draft={fieldDraft} />
        ) : (
          <ChatWelcomeScreen />
        )
      ) : (
        <ChatMessageList
          messages={messages}
          status={status}
          onFeedback={handleFeedback}
        />
      )}

      <TaskList tasks={tasks} />

      {(error || initError) && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="caption" color="error">
            {error?.message || initError?.message}
          </Typography>
        </Box>
      )}

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        {approval && !isStreaming ? (
          <ToolApprovalCard
            approval={approval}
            disabled={isInitializing}
            onRespond={handleApproval}
          />
        ) : questions ? (
          <AgentQuestionForm
            questions={questions}
            disabled={isInitializing || isStreaming}
            onSubmit={handleAgentQuestionSubmit}
          />
        ) : (
          <ChatBottomPanel
            onSend={handleSend}
            onStop={handleStop}
            isStreaming={isStreaming}
            isInitializing={isInitializing}
            focusTrigger={lastStreamFinished}
            mcpReadWrite={mcpReadWrite}
            attachments={attachments}
          />
        )}
      </Box>
    </Stack>
  );
};

export const ChatPanel = ({ open, onClose }: ChatPanelProps) => (
  <ChatDrawerShell open={open} onClose={onClose}>
    <ChatPanelContent open={open} onClose={onClose} />
  </ChatDrawerShell>
);
